/**
 * XOR Share Generator for ORIFOLI
 * Based on Figryne's XOR-Pix encryption system
 */

// ============================================
// 型定義
// ============================================

export interface XorShares {
  share1: Blob;
  share2: Blob;
  original: Blob;
  width: number;
  height: number;
}

/**
 * シード付きセキュアXORシェア
 * シードはサーバーに保存し、表示時に認証後取得する
 */
export interface SecureXorShares extends XorShares {
  /** 暗号化シード（サーバー保存用） */
  encryptionSeed: string;
}

export interface XorShareOptions {
  maxDimension?: number;
  quality?: number;
  /** 再現可能なノイズ生成用シード（Share1生成用） */
  seed?: string;
}

export interface SecureXorShareOptions extends XorShareOptions {
  /** 暗号化シードを指定（省略時は自動生成） */
  encryptionSeed?: string;
}

// ============================================
// 乱数生成器
// ============================================

/**
 * Mulberry32 - シード付き疑似乱数生成器
 * 高速かつ十分な品質の疑似乱数を生成
 */
function mulberry32(seed: number): () => number {
  return function() {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/**
 * 文字列をシード値（数値）に変換
 */
function stringToSeed(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash) || 1; // 0を避ける
}

/**
 * 暗号学的に安全なランダムシードを生成
 */
function generateSecureRandomSeed(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

// ============================================
// 画像処理ユーティリティ
// ============================================

/**
 * 画像ファイルをリサイズしてImageDataを取得
 */
async function getResizedImageData(
  file: File,
  maxDimension: number
): Promise<{ imageData: ImageData; width: number; height: number; canvas: HTMLCanvasElement }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let width = img.width;
      let height = img.height;

      // リサイズが必要な場合
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      const imageData = ctx.getImageData(0, 0, width, height);

      resolve({ imageData, width, height, canvas });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * ImageDataをBlobに変換
 */
function imageDataToBlob(
  imageData: ImageData,
  width: number,
  height: number,
  quality: number = 0.92
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    ctx.putImageData(imageData, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/png',
      quality
    );
  });
}

/**
 * CanvasをBlobに変換
 */
function canvasToBlob(canvas: HTMLCanvasElement, quality: number = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/png',
      quality
    );
  });
}

/**
 * BlobをImageDataに変換
 */
async function blobToImageData(blob: Blob): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      resolve(ctx.getImageData(0, 0, img.width, img.height));
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

// ============================================
// セキュアXOR関数（シード付き暗号化）
// ============================================

/**
 * 画像ファイルからセキュアXORシェアを生成
 *
 * Share2 = Original XOR Share1 XOR SeedNoise
 * → シードがないとShare1 XOR Share2しても元画像は復元不可能
 *
 * @param file - 元画像ファイル
 * @param options - オプション
 * @returns SecureXorShares - share1, share2, original, encryptionSeed
 */
export async function generateSecureXorShares(
  file: File,
  options: SecureXorShareOptions = {}
): Promise<SecureXorShares> {
  const {
    maxDimension = 2048,
    quality = 0.92,
    seed,
    encryptionSeed = generateSecureRandomSeed()
  } = options;

  // 画像を読み込んでリサイズ
  const { imageData, width, height, canvas } = await getResizedImageData(file, maxDimension);
  const original = imageData.data;

  // シェア用のImageDataを作成
  const share1Data = new ImageData(width, height);
  const share2Data = new ImageData(width, height);
  const share1 = share1Data.data;
  const share2 = share2Data.data;

  // Share1用の乱数生成器（シード指定があれば再現可能）
  const random1 = seed ? mulberry32(stringToSeed(seed)) : Math.random;

  // 暗号化用の乱数生成器（encryptionSeedから決定論的に生成）
  const seedRandom = mulberry32(stringToSeed(encryptionSeed));

  // XOR分割（シード付き）
  for (let i = 0; i < original.length; i += 4) {
    // Share1用ノイズ
    const noise1R = Math.floor(random1() * 256);
    const noise1G = Math.floor(random1() * 256);
    const noise1B = Math.floor(random1() * 256);

    // 暗号化用シードノイズ
    const seedNoiseR = Math.floor(seedRandom() * 256);
    const seedNoiseG = Math.floor(seedRandom() * 256);
    const seedNoiseB = Math.floor(seedRandom() * 256);

    // Share1 = ランダムノイズ
    share1[i] = noise1R;
    share1[i + 1] = noise1G;
    share1[i + 2] = noise1B;
    share1[i + 3] = 255;

    // Share2 = Original XOR Share1Noise XOR SeedNoise
    share2[i] = original[i] ^ noise1R ^ seedNoiseR;
    share2[i + 1] = original[i + 1] ^ noise1G ^ seedNoiseG;
    share2[i + 2] = original[i + 2] ^ noise1B ^ seedNoiseB;
    share2[i + 3] = 255;
  }

  // Blobに変換
  const [share1Blob, share2Blob, originalBlob] = await Promise.all([
    imageDataToBlob(share1Data, width, height, quality),
    imageDataToBlob(share2Data, width, height, quality),
    canvasToBlob(canvas, quality)
  ]);

  return {
    share1: share1Blob,
    share2: share2Blob,
    original: originalBlob,
    width,
    height,
    encryptionSeed
  };
}

/**
 * セキュアXORシェアを合成してImageDataを直接返す
 * ネットワークタブに画像が表示されないようにするための関数
 *
 * @param share1Blob - Share1
 * @param share2Blob - Share2
 * @param encryptionSeed - 暗号化シード（サーバーから取得）
 * @returns 復元されたImageDataと寸法
 */
export async function combineSecureXorSharesToImageData(
  share1Blob: Blob,
  share2Blob: Blob,
  encryptionSeed: string
): Promise<{ imageData: ImageData; width: number; height: number }> {
  const [share1Data, share2Data] = await Promise.all([
    blobToImageData(share1Blob),
    blobToImageData(share2Blob)
  ]);

  if (share1Data.width !== share2Data.width || share1Data.height !== share2Data.height) {
    throw new Error('Share dimensions do not match');
  }

  const width = share1Data.width;
  const height = share1Data.height;
  const share1 = share1Data.data;
  const share2 = share2Data.data;

  // 暗号化シードからノイズを再生成
  const seedRandom = mulberry32(stringToSeed(encryptionSeed));

  const resultData = new ImageData(width, height);
  const result = resultData.data;

  // XOR合成（シード付き）
  for (let i = 0; i < share1.length; i += 4) {
    const seedNoiseR = Math.floor(seedRandom() * 256);
    const seedNoiseG = Math.floor(seedRandom() * 256);
    const seedNoiseB = Math.floor(seedRandom() * 256);

    result[i] = share1[i] ^ share2[i] ^ seedNoiseR;
    result[i + 1] = share1[i + 1] ^ share2[i + 1] ^ seedNoiseG;
    result[i + 2] = share1[i + 2] ^ share2[i + 2] ^ seedNoiseB;
    result[i + 3] = 255;
  }

  return { imageData: resultData, width, height };
}

/**
 * セキュアシェア生成の進捗を追跡しながら実行
 */
export async function generateSecureXorSharesWithProgress(
  file: File,
  options: SecureXorShareOptions = {},
  onProgress?: (progress: number, stage: string) => void
): Promise<SecureXorShares> {
  onProgress?.(0, 'loading');

  const {
    maxDimension = 2048,
    quality = 0.92,
    seed,
    encryptionSeed = generateSecureRandomSeed()
  } = options;

  // 画像を読み込んでリサイズ
  onProgress?.(10, 'resizing');
  const { imageData, width, height, canvas } = await getResizedImageData(file, maxDimension);
  const original = imageData.data;

  onProgress?.(25, 'generating_seed');

  // シェア用のImageDataを作成
  const share1Data = new ImageData(width, height);
  const share2Data = new ImageData(width, height);
  const share1 = share1Data.data;
  const share2 = share2Data.data;

  // Share1用の乱数生成器
  const random1 = seed ? mulberry32(stringToSeed(seed)) : Math.random;

  // 暗号化用の乱数生成器
  const seedRandom = mulberry32(stringToSeed(encryptionSeed));

  onProgress?.(30, 'encrypting');

  // XOR分割（シード付き）
  const totalPixels = original.length / 4;
  const chunkSize = 50000;

  for (let i = 0; i < original.length; i += 4) {
    // Share1用ノイズ
    const noise1R = Math.floor(random1() * 256);
    const noise1G = Math.floor(random1() * 256);
    const noise1B = Math.floor(random1() * 256);

    // 暗号化用シードノイズ
    const seedNoiseR = Math.floor(seedRandom() * 256);
    const seedNoiseG = Math.floor(seedRandom() * 256);
    const seedNoiseB = Math.floor(seedRandom() * 256);

    // Share1 = ランダムノイズ
    share1[i] = noise1R;
    share1[i + 1] = noise1G;
    share1[i + 2] = noise1B;
    share1[i + 3] = 255;

    // Share2 = Original XOR Share1Noise XOR SeedNoise
    share2[i] = original[i] ^ noise1R ^ seedNoiseR;
    share2[i + 1] = original[i + 1] ^ noise1G ^ seedNoiseG;
    share2[i + 2] = original[i + 2] ^ noise1B ^ seedNoiseB;
    share2[i + 3] = 255;

    // 進捗更新
    if (i % (chunkSize * 4) === 0) {
      const pixelProgress = (i / 4) / totalPixels;
      onProgress?.(30 + pixelProgress * 40, 'encrypting');
    }
  }

  onProgress?.(70, 'encoding');

  // Blobに変換
  const [share1Blob, share2Blob, originalBlob] = await Promise.all([
    imageDataToBlob(share1Data, width, height, quality),
    imageDataToBlob(share2Data, width, height, quality),
    canvasToBlob(canvas, quality)
  ]);

  onProgress?.(100, 'complete');

  return {
    share1: share1Blob,
    share2: share2Blob,
    original: originalBlob,
    width,
    height,
    encryptionSeed
  };
}

// ============================================
// WebGL用ユーティリティ
// ============================================

/**
 * シードからシードノイズテクスチャを生成
 * WebGLで使用するためのImageDataを生成
 */
export function generateSeedNoiseImageData(
  encryptionSeed: string,
  width: number,
  height: number
): ImageData {
  const seedRandom = mulberry32(stringToSeed(encryptionSeed));
  const imageData = new ImageData(width, height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    data[i] = Math.floor(seedRandom() * 256);     // R
    data[i + 1] = Math.floor(seedRandom() * 256); // G
    data[i + 2] = Math.floor(seedRandom() * 256); // B
    data[i + 3] = 255;                             // A
  }

  return imageData;
}

// ============================================
// WebGL高速合成（GPU使用）
// ============================================

const VERTEX_SHADER = `
  attribute vec2 aPosition;
  varying vec2 vTexCoord;
  void main() {
    vTexCoord = aPosition * 0.5 + 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`;

/**
 * WebGLフラグメントシェーダー用のXOR合成コード（シード付き）
 */
const WEBGL_SECURE_XOR_FRAGMENT_SHADER = `
  precision mediump float;
  uniform sampler2D uShare1;
  uniform sampler2D uShare2;
  uniform sampler2D uSeedNoise;
  varying vec2 vTexCoord;

  void main() {
    vec4 c1 = texture2D(uShare1, vTexCoord);
    vec4 c2 = texture2D(uShare2, vTexCoord);
    vec4 seedNoise = texture2D(uSeedNoise, vTexCoord);

    vec3 i1 = floor(c1.rgb * 255.0 + 0.5);
    vec3 i2 = floor(c2.rgb * 255.0 + 0.5);
    vec3 iSeed = floor(seedNoise.rgb * 255.0 + 0.5);

    vec3 result = vec3(0.0);
    float factor = 128.0;
    for (int i = 0; i < 8; i++) {
      vec3 bit1 = floor(mod(i1 / factor, 2.0));
      vec3 bit2 = floor(mod(i2 / factor, 2.0));
      vec3 bitSeed = floor(mod(iSeed / factor, 2.0));
      vec3 xorBit = mod(bit1 + bit2 + bitSeed, 2.0);
      result += xorBit * factor;
      factor /= 2.0;
    }

    gl_FragColor = vec4(result / 255.0, 1.0);
  }
`;

/**
 * WebGLコンテキストとシェーダープログラムをセットアップ
 */
function setupWebGL(
  gl: WebGLRenderingContext,
  fragmentShaderSource: string
): WebGLProgram | null {
  // 頂点シェーダー
  const vertexShader = gl.createShader(gl.VERTEX_SHADER);
  if (!vertexShader) return null;
  gl.shaderSource(vertexShader, VERTEX_SHADER);
  gl.compileShader(vertexShader);
  if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
    console.error('Vertex shader error:', gl.getShaderInfoLog(vertexShader));
    return null;
  }

  // フラグメントシェーダー
  const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
  if (!fragmentShader) return null;
  gl.shaderSource(fragmentShader, fragmentShaderSource);
  gl.compileShader(fragmentShader);
  if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
    console.error('Fragment shader error:', gl.getShaderInfoLog(fragmentShader));
    return null;
  }

  // プログラム
  const program = gl.createProgram();
  if (!program) return null;
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program));
    return null;
  }

  return program;
}

/**
 * ImageDataからWebGLテクスチャを作成
 */
function createTextureFromImageData(
  gl: WebGLRenderingContext,
  imageData: ImageData
): WebGLTexture | null {
  const texture = gl.createTexture();
  if (!texture) return null;

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imageData);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

  return texture;
}

/**
 * WebGL（GPU）を使用してセキュアXORシェアを高速合成
 * CPUループの代わりにGPUで並列処理
 */
export async function combineSecureXorSharesWebGL(
  share1Blob: Blob,
  share2Blob: Blob,
  encryptionSeed: string
): Promise<{ imageData: ImageData; width: number; height: number }> {
  // Blobを読み込み
  const [share1Data, share2Data] = await Promise.all([
    blobToImageData(share1Blob),
    blobToImageData(share2Blob)
  ]);

  if (share1Data.width !== share2Data.width || share1Data.height !== share2Data.height) {
    throw new Error('Share dimensions do not match');
  }

  const width = share1Data.width;
  const height = share1Data.height;

  // シードノイズを生成
  const seedNoiseData = generateSeedNoiseImageData(encryptionSeed, width, height);

  // WebGLキャンバスを作成
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true });

  if (!gl) {
    // WebGL非対応の場合はCPUフォールバック
    console.warn('[XorShare] WebGL not available, falling back to CPU');
    return combineSecureXorSharesToImageData(share1Blob, share2Blob, encryptionSeed);
  }

  // シェーダープログラムをセットアップ
  const program = setupWebGL(gl, WEBGL_SECURE_XOR_FRAGMENT_SHADER);
  if (!program) {
    console.warn('WebGL program setup failed, falling back to CPU');
    return combineSecureXorSharesToImageData(share1Blob, share2Blob, encryptionSeed);
  }

  gl.useProgram(program);

  // 頂点バッファ（フルスクリーンクワッド）
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1
  ]), gl.STATIC_DRAW);

  const aPosition = gl.getAttribLocation(program, 'aPosition');
  gl.enableVertexAttribArray(aPosition);
  gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

  // テクスチャを作成
  const tex1 = createTextureFromImageData(gl, share1Data);
  const tex2 = createTextureFromImageData(gl, share2Data);
  const texSeed = createTextureFromImageData(gl, seedNoiseData);

  if (!tex1 || !tex2 || !texSeed) {
    throw new Error('Failed to create textures');
  }

  // テクスチャユニットにバインド
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, tex1);
  gl.uniform1i(gl.getUniformLocation(program, 'uShare1'), 0);

  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, tex2);
  gl.uniform1i(gl.getUniformLocation(program, 'uShare2'), 1);

  gl.activeTexture(gl.TEXTURE2);
  gl.bindTexture(gl.TEXTURE_2D, texSeed);
  gl.uniform1i(gl.getUniformLocation(program, 'uSeedNoise'), 2);

  // ビューポート設定
  gl.viewport(0, 0, width, height);

  // 描画
  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // 結果を読み取り
  const pixels = new Uint8Array(width * height * 4);
  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

  // ImageDataに直接コピー
  const imageData = new ImageData(width, height);
  imageData.data.set(pixels);

  // クリーンアップ
  gl.deleteTexture(tex1);
  gl.deleteTexture(tex2);
  gl.deleteTexture(texSeed);
  gl.deleteBuffer(positionBuffer);
  gl.deleteProgram(program);

  return { imageData, width, height };
}
